import * as React from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { switchState } from '../../slices/stateSlice';
import { Landing } from '../Landing/Landing';
import { hideMap } from '../../slices/mapSlice';
import { SiteState } from '../../enums';
import { addPlayer, updatePlayer, clearScene, removePlayer, setScene } from '../../slices/sceneSlice';
import { batch } from 'react-redux';
import { setStats } from '../../slices/statSlice';
import { patchInventory, setItems } from '../../slices/inventorySlice';
import { setKnownSkills, setEquippedSkills } from '../../slices/skillsSlice';
import { setQuests } from '../../slices/questsSlice';
import { setShops } from '../../slices/shopSlice';
import { setHomeVillage, setMultiFight, setSlotFight, setDisplayName } from '../../slices/uiSlice';
import { applyItemOverrides } from '../../util/assetConfig';
import store from '../../store';
import { SERVER_CONFIG, setServerConfig } from '../../util/serverConfig';
import { setDungeon } from '../../slices/dungeonSlice';
import { setMedals, setPots, setHistory } from '../../slices/arenaSlice';
import { play } from '../../util/MusicPlayer';
import { setCharacterPartial } from '../../slices/characterSlice';
import { setParty } from '../../slices/partySlice';
import { GameLoader } from '../GameLoader/GameLoader';
import { Game } from '../Game/Game';
import { cancelFromServer, fromServer, logOff, toServer } from '../../util/ServerSocket';
import { setStones } from '../../slices/currencySlice';

const SiteStateFactory: React.FC<{}> = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.state.state);

  React.useEffect(() => {
    fromServer('addPlayerToScene', (data) => dispatch(addPlayer(data)));
    fromServer('updateScenePlayer', (data) => dispatch(updatePlayer(data)));
    fromServer('inventoryPatch', (data) => dispatch(patchInventory(data)));
    fromServer('partyUpdate', (party) => dispatch(setParty(party)));
    fromServer('removePlayerFromScene', (data) => dispatch(removePlayer(data)));
    fromServer('updateCharacter', (data) => { dispatch(setCharacterPartial(data)); if (data.displayName !== undefined) dispatch(setDisplayName(data.displayName)); if(data.skills!==undefined)dispatch(setEquippedSkills(data.skills)); if(data.skillsKnown!==undefined)dispatch(setKnownSkills(data.skillsKnown)); });
    fromServer('itemCatalogPatch', (data) => {
      applyItemOverrides(data);
      const inventory = store.getState().inventory;
      dispatch(setItems({ ...inventory, items: { ...inventory.items } }));
    });
    fromServer('updateDungeon', (data) => dispatch(setDungeon(data)));
    fromServer('updateItems', (data) => dispatch(setItems(data)));
    fromServer('updateMultiFight', (data) => dispatch(setMultiFight(data)));
    fromServer('updateVillageSelection', (data) => dispatch(setHomeVillage(data)));
    fromServer('updateShops', (data) => dispatch(setShops(data)));
    fromServer('shopCatalogChanged', () => toServer('shop'));
    fromServer('updatePotShops', (data) => dispatch(setPots(data)));
    fromServer('updateSkillsKnown', (data) => dispatch(setKnownSkills(data)));
    fromServer('updateSlots', (data) => dispatch(setSlotFight(data)));
    fromServer('updateStats', (data) => dispatch(setStats(data)));
    fromServer('levelCapChanged', (data) => {
      if (!Number.isSafeInteger(data?.max) || data.max < 1 || data.serverId !== store.getState().account.serverId) return;
      setServerConfig('LEVELING', { ...SERVER_CONFIG.LEVELING, max: data.max });
      dispatch(setStats({}));
    });
    fromServer('updateStones', (data) => dispatch(setStones(data)));
    fromServer('updateMedals', (data) => dispatch(setMedals(data)));
    fromServer('updateQuests', (data) => dispatch(setQuests(data)));
    fromServer('relog', () => {
      logOff();
      batch(() => {
        dispatch(clearScene());
        dispatch(switchState(SiteState.LANDING));
      });
    });
    fromServer('updateArenaHistory', (data) => dispatch(setHistory(data)));
    fromServer('requestSwitchScene', (scene) => toServer('switchScene', scene));
    fromServer('switchScene', (data) => {
      batch(() => {
        dispatch(hideMap());
        dispatch(setScene(data));
        play(data.scene);
      });
    });

    return () => {
      cancelFromServer('addPlayerToScene');
      cancelFromServer('updateScenePlayer');
      cancelFromServer('inventoryPatch');
      cancelFromServer('partyUpdate');
      cancelFromServer('removePlayerFromScene');
      cancelFromServer('updateCharacter');
      cancelFromServer('itemCatalogPatch');
      cancelFromServer('updateDungeon');
      cancelFromServer('updateItems');
      cancelFromServer('updateMultiFight');
      cancelFromServer('updateParty');
      cancelFromServer('updateVillageSelection');
      cancelFromServer('updateShops');
      cancelFromServer('shopCatalogChanged');
      cancelFromServer('updatePotShops');
      cancelFromServer('updateSkillsKnown');
      cancelFromServer('updateSlots');
      cancelFromServer('updateStats');
      cancelFromServer('levelCapChanged');
      cancelFromServer('updateStones');
      cancelFromServer('updateMedals');
      cancelFromServer('updateQuests');
      cancelFromServer('relog');
      cancelFromServer('requestSwitchScene');
      cancelFromServer('switchScene');
    };
  }, [dispatch]);

  function getContent() {
    switch (state) {
      case SiteState.GAME:
        return <Game />;
      case SiteState.GAME_LOADER:
        return <GameLoader />;
      default:
        return <Landing />;
    }
  }

  const content = React.useMemo(getContent, [state]);

  return content;
};

export default SiteStateFactory;
